import React, { useState, useEffect, useCallback, useMemo } from "react";
import "../../ManagerDashboard.css";
import DataTable from "../../Robuts Components/DataTable";
import { Button, Alert } from "react-bootstrap";
// import { convertToDate } from "../../../../backend/timeFunctions";
import Joyride, { STATUS } from "react-joyride";
import { FaQuestionCircle, FaUserSlash, FaUser } from "react-icons/fa";
import ScrollToTop from "../../Robuts Components/ScrollToTop";
import Select from "react-select";
import { updateStudent, db } from "../../../../backend/firebaseFunctions";
import { Timestamp } from "firebase/firestore";
import CustomModal from "../../../Global/Modal/CustomModal";
// import TipCard from "../../Robuts Components/TipCard";
import StudentDetailsModal from "./StudentDetailsModal";
import CustomAlert from "../../Robuts Components/CustomAlert";

// Helper function to calculate age from birthday timestamp
const getAge = (birthday) => {
  try {
    if (!birthday) return null;

    // Use the timestamp conversion helper
    const birthDate = convertTimestampToDate(birthday);
    if (!birthDate) return null;

    const currentDate = new Date();
    let age = currentDate.getFullYear() - birthDate.getFullYear();

    if (
      currentDate.getMonth() < birthDate.getMonth() ||
      (currentDate.getMonth() === birthDate.getMonth() &&
        currentDate.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age >= 0 ? age : null;
  } catch (error) {
    console.error("Error calculating age:", error);
    return null;
  }
};

// Helper to safely convert Firestore timestamp objects to Date objects
const convertTimestampToDate = (timestamp) => {
  try {
    if (!timestamp) return null;

    // Handle Firebase Timestamp objects
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }

    // Handle plain objects with seconds/nanoseconds (from Firestore)
    if (timestamp.seconds && typeof timestamp.seconds === 'number') {
      const milliseconds = timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1e6;
      return new Date(milliseconds);
    }

    // Handle Date objects
    if (timestamp instanceof Date) {
      return timestamp;
    }

    // Handle string dates
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    }

    // Handle numeric timestamps
    if (typeof timestamp === 'number') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  } catch (error) {
    console.error("Error converting timestamp to date:", error);
    return null;
  }
};

// Helper to format how long ago a date was, in Hebrew
function formatHebrewTimeAgo(fromDate, toDate = new Date()) {
  try {
    if (!fromDate) return "";

    // Convert timestamp to Date object first
    const normalizedFromDate = convertTimestampToDate(fromDate);
    if (!normalizedFromDate) return "";

    let years = toDate.getFullYear() - normalizedFromDate.getFullYear();
    let months = toDate.getMonth() - normalizedFromDate.getMonth();
    let days = toDate.getDate() - normalizedFromDate.getDate();

    if (days < 0) {
      months--;
      // Get days in previous month
      const prevMonth = new Date(toDate.getFullYear(), toDate.getMonth(), 0);
      days += prevMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    // If the day of the month is the same or earlier, count the month as complete
    if (toDate.getDate() >= normalizedFromDate.getDate()) {
      months += 1;
      if (months === 12) {
        years += 1;
        months = 0;
      }
    }

    // Pluralization logic
    const yearLabel = years === 1 ? "שנה" : "שנים";
    const monthLabel = months === 1 ? "חודש" : "חודשים";
    const dayLabel = days === 1 ? "יום" : "ימים";

    let parts = [];
    if (years > 0) parts.push(`${years} ${yearLabel}`);
    if (months > 0 && years < 5) parts.push(`${months} ${monthLabel}`);
    if (years === 0 && months === 0 && days > 0)
      parts.push(`${days} ${dayLabel}`);
    if (parts.length === 0) return "היום";
    return `לפני ${parts.join(" ו-")}`;
  } catch (error) {
    console.error("Error formatting Hebrew time ago:", error);
    return "";
  }
}

function StudentsPane({
  students,
  groups,
  setCurrentTab,
  userData,
  presenceLogs,
}) {

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentDetailsModal, setShowStudentDetailsModal] = useState(false);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(-1);
  // Remove localStudents state
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedStudent, setEditedStudent] = useState(null);
  const [runTour, setRunTour] = useState(false);
  const [editError, setEditError] = useState("");
  const [alert, setAlert] = useState(null);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);

  // Clear alert after 5 seconds
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Clear edit error after 5 seconds
  useEffect(() => {
    if (editError) {
      const timer = setTimeout(() => {
        setEditError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [editError]);

  // Safe access to userData properties
  const hasViewPrivilege = useMemo(() => {
    try {
      return (
        userData?.userType === "manager" ||
        userData?.coachData?.accessPrivilege?.students?.view ||
        (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
        false
      );
    } catch (error) {
      console.error("Error checking view privilege:", error);
      return false;
    }
  }, [userData]);

  const hasEditPrivilege = useMemo(() => {
    try {
      return (
        userData?.userType === "manager" ||
        userData?.coachData?.accessPrivilege?.students?.write ||
        (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
        false
      );
    } catch (error) {
      console.error("Error checking edit privilege:", error);
      return false;
    }
  }, [userData]);

  // Helper to get the correct UID for student actions
  const getTargetUserID = useCallback(() => {
    try {
      if (
        userData &&
        userData.userType === "coach" &&
        userData.userOrganization?.organizationOwnerID
      ) {
        return userData.userOrganization.organizationOwnerID;
      }
      return userData?.userId || null;
    } catch (error) {
      console.error("Error getting target user ID:", error);
      return null;
    }
  }, [userData]);

  // Tour button style
  const tourButtonStyle = {
    position: "fixed",
    top: "20px",
    left: "20px",
    zIndex: 1000,
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
  };

  // Tour steps
  const tourSteps = [
    {
      target: ".students-wrapper",
      content:
        "ברוכים הבאים לממשק ניהול הספורטאים! כאן תוכלו לצפות ולנהל את כל הספורטאים במערכת.",
      disableBeacon: true,
      placement: "center",
      styles: {
        options: {
          zIndex: 10000,
        },
      },
    },
    {
      target: ".search-bar",
      content:
        "השתמשו בשורת החיפוש כדי למצוא ספורטאים לפי שם, אימייל או מספר טלפון.",
      placement: "bottom",
    },
    {
      target: ".add-student-button",
      content: "לחצו כאן כדי להוסיף חניך חדש למערכת.",
      placement: "bottom",
    },
    {
      target: ".student-table",
      content:
        "בטבלה זו תוכלו לראות את כל הספורטאים. תוכלו לסנן, למיין ולחפש במידע.",
      placement: "top",
    },
  ];

  // Handle tour completion
  const handleTourComplete = ({ status }) => {
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  // Preprocess students data to include calculated age and flattened special fields
  // Now done inline, not in local state
  const processedStudents = useMemo(() => {
    try {
      // Safe check for students array
      if (!Array.isArray(students) || students.length === 0) {
        return [];
      }

      // Filter to only include active students with safe status check
      const activeStudents = students.filter((student) => {
        try {
          return student && student.status === "active";
        } catch (error) {
          console.error("Error filtering student:", error, student);
          return false;
        }
      });

      return activeStudents.map((student) => {
        try {
          if (!student || typeof student !== "object") {
            console.warn("Invalid student object:", student);
            return null;
          }

          const processedStudent = {
            ...student,
            age: getAge(student.birthday),
            specialFields: student.specialFields || {},
          };

          // Safely access userField
          const userField = userData?.userField;

          if (userField === "martial_arts") {
            if (student.specialFields?.beltLevel) {
              processedStudent.beltLevel = student.specialFields.beltLevel;
            }
            if (student.specialFields?.weightKG) {
              processedStudent.weightKG = student.specialFields.weightKG;
            }
          } else if (userField === "ball_games") {
            if (student.specialFields?.shirtSize) {
              processedStudent.shirtSize = student.specialFields.shirtSize;
            }
            if (student.specialFields?.pantsSize) {
              processedStudent.pantsSize = student.specialFields.pantsSize;
            }
          }

          // Add formatted studentCreatedAt for display
          if (student?.studentCreatedAt) {
            const createdAtDate = convertTimestampToDate(student.studentCreatedAt);
            if (createdAtDate) {
              processedStudent.formattedCreatedAt = createdAtDate.toLocaleString("he-IL", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }).replace(",", "");
            }
          }

          return processedStudent;
        } catch (error) {
          console.error("Error processing student:", error, student);
          return null;
        }
      }).filter(Boolean); // Remove any null entries
    } catch (error) {
      console.error("Error processing students data:", error);
      return [];
    }
  }, [students, userData?.userField]);

  // Update navigation and selection logic to use processedStudents
  const navigateToStudent = useCallback((index) => {
    try {
      if (!Array.isArray(processedStudents) || index < 0 || index >= processedStudents.length) {
        console.warn("Invalid index for navigateToStudent:", { index, processedStudentsLength: processedStudents?.length });
        return;
      }

      const student = processedStudents[index];
      if (!student) {
        console.warn("No student found at index:", index);
        return;
      }

      // Validate student has required fields
      if (!student.id) {
        console.warn("Student missing ID at index:", index, student);
        return;
      }

      // Normalize groupsIDs safely
      let normalizedGroupsIDs = [];
      if (student.groupsIDs) {
        try {
          if (Array.isArray(student.groupsIDs)) {
            normalizedGroupsIDs = student.groupsIDs.map((groupIdOrName) => {
              if (typeof groupIdOrName === "string" && Array.isArray(groups)) {
                const group = groups.find((g) => g && g.name === groupIdOrName);
                return group ? group.id : groupIdOrName;
              }
              return groupIdOrName;
            });
          } else if (typeof student.groupsIDs === "string" && Array.isArray(groups)) {
            const group = groups.find((g) => g && g.name === student.groupsIDs);
            normalizedGroupsIDs = group ? [group.id] : [student.groupsIDs];
          }
        } catch (error) {
          console.error("Error normalizing groupsIDs:", error);
          normalizedGroupsIDs = [];
        }
      }

      const completeStudent = {
        ...student,
        groupsIDs: normalizedGroupsIDs,
      };

      setCurrentStudentIndex(index);
      setSelectedStudent(completeStudent);
    } catch (error) {
      console.error("Error navigating to student:", error);
      // Reset state on error
      setCurrentStudentIndex(-1);
      setSelectedStudent(null);
    }
  }, [processedStudents, groups]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showStudentDetailsModal || isEditMode) return;

      if (e.key === "Escape") {
        setShowStudentDetailsModal(false);
        setSelectedStudent(null);
        setCurrentStudentIndex(-1);
        setIsEditMode(false);
        setEditedStudent(null);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (currentStudentIndex > 0) {
          navigateToStudent(currentStudentIndex - 1);
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (currentStudentIndex < processedStudents.length - 1) {
          navigateToStudent(currentStudentIndex + 1);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    showStudentDetailsModal,
    currentStudentIndex,
    navigateToStudent,
    isEditMode,
    processedStudents.length,
  ]);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      try {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        setShowScrollTop(scrollTop > 300);
      } catch (error) {
        console.error("Error handling scroll:", error);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    try {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error("Error scrolling to top:", error);
    }
  };

  // Define tour steps
  const steps = [
    {
      target: ".students-wrapper",
      content:
        "ברוכים הבאים למסך הספורטאים! כאן תוכלו לנהל את כל הספורטאים בארגון, לצפות בפרטיהם ולעקוב אחר התקדמותם. בואו נעשה סיור קצר!",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="new-students-button"]',
      content:
        "לחצו כאן כדי לצפות בספורטאים חדשים הממתינים לאישור. המספר האדום מציין כמה ספורטאים ממתינים.",
      placement: "bottom",
    },
    {
      target: ".students-data-table",
      content:
        "כאן תוכלו לראות את כל הספורטאים הרשומים. לחצו על חניך כדי לראות את כל הפרטים שלו, כולל פרטי קשר של ההורים.",
      placement: "bottom",
    },
    {
      target: ".students-guide-button",
      content: "אם תרצו לראות את הסיור שוב, תוכלו ללחוץ על כפתור זה בכל עת!",
      placement: "left",
      spotlightClicks: true,
    },
  ];

  // Handle tour events
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  const handleTourButtonClick = () => {
    setRunTour(true);
  };

  // Handle when DataTable applies filtering
  const handleFilteredDataChange = (filteredData) => {
    // We don't need to do anything here as filtering is handled by DataTable
  };

  // Update onStudentRowClick to use processedStudents
  const onStudentRowClick = useCallback((student) => {
    try {
      if (!student || !Array.isArray(processedStudents)) {
        console.warn("Invalid student or processedStudents in onStudentRowClick:", { student, processedStudents });
        return;
      }

      // Validate student has required fields
      if (!student.id) {
        console.warn("Student missing ID:", student);
        return;
      }

      // Normalize groupsIDs to always be an array of IDs
      let normalizedGroupsIDs = [];
      if (student.groupsIDs) {
        try {
          if (Array.isArray(student.groupsIDs)) {
            normalizedGroupsIDs = student.groupsIDs.map((groupIdOrName) => {
              if (typeof groupIdOrName === "string" && Array.isArray(groups)) {
                const group = groups.find((g) => g && g.name === groupIdOrName);
                return group ? group.id : groupIdOrName;
              }
              return groupIdOrName;
            });
          } else if (typeof student.groupsIDs === "string" && Array.isArray(groups)) {
            const group = groups.find((g) => g && g.name === student.groupsIDs);
            normalizedGroupsIDs = group ? [group.id] : [student.groupsIDs];
          }
        } catch (error) {
          console.error("Error normalizing groupsIDs in row click:", error);
          normalizedGroupsIDs = [];
        }
      }

      const completeStudent = {
        ...student,
        groupsIDs: normalizedGroupsIDs,
      };

      const index = processedStudents.findIndex((s) => s && s.id === student.id);
      if (index === -1) {
        console.warn("Student not found in processedStudents:", student.id);
        return;
      }

      setCurrentStudentIndex(index);
      setSelectedStudent(completeStudent);
      setShowStudentDetailsModal(true);
    } catch (error) {
      console.error("Error handling student row click:", error);
      // Reset modal state on error
      setShowStudentDetailsModal(false);
      setSelectedStudent(null);
      setCurrentStudentIndex(-1);
    }
  }, [processedStudents, groups]);

  // This function checks how many students are waiting for acceptence. It is used to render a number badge.
  const calcNumberOfNewStudents = useCallback(() => {
    try {
      if (!Array.isArray(students)) return 0;
      return students.filter((student) => student?.status === "toReview").length;
    } catch (error) {
      console.error("Error calculating new students count:", error);
      return 0;
    }
  }, [students]);

  // Add this function to calculate number of archived students
  const calcNumberOfArchivedStudents = useCallback(() => {
    try {
      if (!Array.isArray(students)) return 0;
      return students.filter((student) => student?.status === "not active").length;
    } catch (error) {
      console.error("Error calculating archived students count:", error);
      return 0;
    }
  }, [students]);

  const handleEditClick = () => {
    try {
      if (selectedStudent) {
        setEditedStudent({ ...selectedStudent });
        setIsEditMode(true);
      }
    } catch (error) {
      console.error("Error starting edit mode:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditedStudent(null);
    setIsEditMode(false);
  };

  // Belt, shirt, and pants options (from StudentDetailsPhase.js)
  const beltColorOptions = [
    { value: "K10", label: "K10" },
    { value: "K9", label: "K9" },
    { value: "K8", label: "K8" },
    { value: "K7", label: "K7" },
    { value: "K6", label: "K6" },
    { value: "K5", label: "K5" },
    { value: "K4", label: "K4" },
    { value: "K3", label: "K3" },
    { value: "K2", label: "K2" },
    { value: "K1", label: "K1" },
    { value: "D1", label: "D1" },
    { value: "D2", label: "D2" },
    { value: "D3", label: "D3" },
    { value: "D4", label: "D4" },
    { value: "D5", label: "D5" },
    { value: "D6", label: "D6" },
    { value: "D7", label: "D7" },
    { value: "D8", label: "D8" },
    { value: "D9", label: "D9" },
    { value: "D10", label: "D10" },
  ];
  const shirtSizeOptions = [
    { value: "XXS", label: "XXS" },
    { value: "XS", label: "XS" },
    { value: "S", label: "S" },
    { value: "M", label: "M" },
    { value: "L", label: "L" },
    { value: "XL", label: "XL" },
    { value: "XXL", label: "XXL" },
  ];
  const pantsSizeOptions = Array.from({ length: 26 }, (_, i) => {
    const size = 10 + i * 2;
    return { value: size.toString(), label: size.toString() };
  });

  const handleSaveEdit = async () => {
    try {
      if (!editedStudent) return;

      // Validation
      if (editedStudent?.IDnumber && editedStudent.IDnumber.length > 9) {
        setEditError("תעודת זהות לא יכולה להכיל יותר מ-9 תווים.");
        return;
      }
      if (editedStudent?.address && editedStudent.address.length > 40) {
        setEditError("הכתובת לא יכולה להכיל יותר מ-40 תווים.");
        return;
      }
      if (
        editedStudent?.specialFields?.beltLevel &&
        !beltColorOptions.some(
          (opt) => opt.value === editedStudent.specialFields.beltLevel
        )
      ) {
        setEditError("צבע החגורה לא תקין.");
        return;
      }
      if (
        editedStudent?.specialFields?.shirtSize &&
        !shirtSizeOptions.some(
          (opt) => opt.value === editedStudent.specialFields.shirtSize
        )
      ) {
        setEditError("מידת חולצה לא תקינה.");
        return;
      }
      if (
        editedStudent?.specialFields?.pantsSize &&
        !pantsSizeOptions.some(
          (opt) => opt.value === editedStudent.specialFields.pantsSize
        )
      ) {
        setEditError("מידת מכנסיים לא תקינה.");
        return;
      }

      setEditError("");

      const targetUserID = getTargetUserID();
      if (!targetUserID) {
        throw new Error("Unable to determine target user ID");
      }

      // Convert birthday and other date fields to Firestore Timestamp
      const studentToUpdate = { ...editedStudent };
      if (studentToUpdate.birthday) {
        try {
          if (typeof studentToUpdate.birthday === "string") {
            studentToUpdate.birthday = Timestamp.fromDate(
              new Date(studentToUpdate.birthday)
            );
          } else if (studentToUpdate.birthday instanceof Date) {
            studentToUpdate.birthday = Timestamp.fromDate(
              studentToUpdate.birthday
            );
          } else if (
            typeof studentToUpdate.birthday === "object" &&
            studentToUpdate.birthday.seconds
          ) {
            studentToUpdate.birthday = new Timestamp(
              studentToUpdate.birthday.seconds,
              studentToUpdate.birthday.nanoseconds || 0
            );
          }
        } catch (error) {
          console.error("Error converting birthday:", error);
          // Continue without birthday if conversion fails
          delete studentToUpdate.birthday;
        }
      }

      // Ensure studentCreatedAt is preserved as a proper Firestore Timestamp
      if (studentToUpdate?.studentCreatedAt) {
        try {
          const createdAtDate = convertTimestampToDate(studentToUpdate.studentCreatedAt);
          if (createdAtDate) {
            studentToUpdate.studentCreatedAt = Timestamp.fromDate(createdAtDate);
          }
        } catch (error) {
          console.error("Error converting studentCreatedAt:", error);
          // Keep original if conversion fails
        }
      }

      // Actually update the student in Firestore
      await updateStudent(
        targetUserID,
        studentToUpdate.id,
        studentToUpdate
      );

      // Don't update local state - let the real-time listener handle it
      setSelectedStudent(null);
      setIsEditMode(false);
      setEditedStudent(null);
      setAlert({
        type: "success",
        title: "הצלחה",
        message: "החניך עודכן בהצלחה!",
      });
    } catch (error) {
      console.error("Error saving student edit:", error);
      setAlert({
        type: "error",
        title: "שגיאה",
        message: "עדכון החניך נכשל. נסה שוב.",
      });
    }
  };

  const handleFieldChange = (field, value) => {
    try {
      setEditedStudent((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          [field]: value,
        };
      });
    } catch (error) {
      console.error("Error changing field:", error);
    }
  };

  const handleSpecialFieldChange = (field, value) => {
    try {
      setEditedStudent((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          specialFields: {
            ...prev.specialFields,
            [field]: value,
          },
        };
      });
    } catch (error) {
      console.error("Error changing special field:", error);
    }
  };

  // Disable student handler
  const handleDisableStudent = async () => {
    try {
      if (!selectedStudent) return;

      setDisableLoading(true);

      const targetUserID = getTargetUserID();

      if (!targetUserID) {
        throw new Error("Unable to determine target user ID");
      }

      // Convert birthday and other date fields to Firestore Timestamp
      const updatedStudent = { ...selectedStudent, status: "not active" };
      if (updatedStudent.birthday) {
        try {
          if (typeof updatedStudent.birthday === "string") {
            updatedStudent.birthday = Timestamp.fromDate(
              new Date(updatedStudent.birthday)
            );
          } else if (updatedStudent.birthday instanceof Date) {
            updatedStudent.birthday = Timestamp.fromDate(updatedStudent.birthday);
          } else if (
            typeof updatedStudent.birthday === "object" &&
            updatedStudent.birthday.seconds
          ) {
            updatedStudent.birthday = new Timestamp(
              updatedStudent.birthday.seconds,
              updatedStudent.birthday.nanoseconds || 0
            );
          }
        } catch (error) {
          console.error("Error converting birthday in disable:", error);
          delete updatedStudent.birthday;
        }
      }

      // Ensure studentCreatedAt is preserved as a proper Firestore Timestamp
      if (updatedStudent?.studentCreatedAt) {
        try {
          const createdAtDate = convertTimestampToDate(updatedStudent.studentCreatedAt);
          if (createdAtDate) {
            updatedStudent.studentCreatedAt = Timestamp.fromDate(createdAtDate);
          }
        } catch (error) {
          console.error("Error converting studentCreatedAt in disable:", error);
          // Keep original if conversion fails
        }
      }

      await updateStudent(targetUserID, updatedStudent.id, updatedStudent);

      // Don't update local state - let the real-time listener handle it
      setShowStudentDetailsModal(false);
      setAlert({
        type: "success",
        title: "הצלחה",
        message: "החניך הועבר לארכיון בהצלחה!",
      });
    } catch (error) {
      console.error("Error disabling student:", error);
      setAlert({
        type: "error",
        title: "שגיאה",
        message: "העברת החניך לארכיון נכשלה. נסה שוב.",
      });
    } finally {
      setDisableLoading(false);
      setShowDisableConfirm(false);
    }
  };

  // --- MODERN DESIGN STYLES ---
  const mainBg = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%)",
    padding: "32px 0",
  };
  const containerStyle = {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 16px",
  };
  // --- DESIGN STYLES ---
  const headerStyle = {
    fontWeight: 800,
    fontSize: "2.5rem",
    letterSpacing: 1,
    marginBottom: 24,
    textAlign: "center",
  };
  const sectionCardStyle = {
    marginBottom: 40,
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: 24,
    direction: "ltr",
  };
  const addButtonStyle = {
    fontWeight: 700,
    fontSize: "1.2rem",
    borderRadius: 999,
    padding: "16px 40px",
    boxShadow: "0 2px 8px rgba(0,180,80,0.10)",
    marginBottom: 32,
  };
  // --- END MODERN DESIGN STYLES ---

  // Update hasAnyStudents to use processedStudents
  const hasAnyStudents = processedStudents && processedStudents.length > 0;

  // Safety check: if modal is open but no student is selected, close the modal
  useEffect(() => {
    if (showStudentDetailsModal && !selectedStudent) {
      setShowStudentDetailsModal(false);
      setIsEditMode(false);
      setEditedStudent(null);
    }
  }, [showStudentDetailsModal, selectedStudent]);

  // Reset modal state when students data changes significantly
  useEffect(() => {
    if (showStudentDetailsModal && (!Array.isArray(students) || students.length === 0)) {
      setShowStudentDetailsModal(false);
      setIsEditMode(false);
      setEditedStudent(null);
      setSelectedStudent(null);
      setCurrentStudentIndex(-1);
    }
  }, [students, showStudentDetailsModal]);

  // If user doesn't have view privilege, show message and return
  if (!hasViewPrivilege) {
    return (
      <div className="students-wrapper">
        <Alert variant="warning" className="m-3" style={{ direction: "rtl" }}>
          אין לך הרשאות לצפייה בספורטאים. אנא פנה למנהל המערכת לקבלת הרשאות.
        </Alert>
      </div>
    );
  }

  return (
    <div style={mainBg}>
      <style>{`
        .modern-student-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .modern-student-card:hover {
          box-shadow: 0 4px 24px #1976d233 !important;
          transform: translateY(-2px) scale(1.01);
        }
        .modern-section-header-icon {
          font-size: 1.3em;
          margin-left: 8px;
        }
        .modern-add-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          padding: 16px 40px !important;
          box-shadow: 0 2px 8px rgba(0,180,80,0.10) !important;
        }
        .modern-archive-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          padding: 16px 40px !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
          transition: all 0.2s ease !important;
        }
        .modern-archive-btn:hover {
          background-color: #6c757d !important;
          color: white !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }
        .modern-modal .modal-content {
          border-radius: 18px !important;
          background: #f8fafc !important;
        }
        .modern-modal .modal-header {
          border-radius: 18px 18px 0 0 !important;
        }
        .modern-modal .modal-footer {
          border-radius: 0 0 18px 18px !important;
        }
        .modern-form-input, .modern-form-input:focus {
          border-radius: 10px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
        }
        .modern-students-table {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          padding: 16px;
        }
        .student-details-modal .modal-content {
          border-radius: 18px !important;
          background: #f8fafc !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.1) !important;
        }
        .student-details-modal .modal-header {
          border-radius: 18px 18px 0 0 !important;
          background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
          color: white !important;
        }
        .student-details-modal .modal-body {
          padding: 24px !important;
        }
        .student-details-modal .modal-footer {
          border-radius: 0 0 18px 18px !important;
        }
      `}</style>
      <div style={containerStyle}>
        {/* Help (tour) button */}
        <button
          style={tourButtonStyle}
          onClick={handleTourButtonClick}
          title="הפעל סיור מודרך"
          className="tour-guide-button"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
          }}
        >
          <FaQuestionCircle size={20} />
        </button>
        <h1 style={headerStyle}>ספורטאים</h1>
        {hasEditPrivilege && (
          <div
            style={{
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              gap: 24,
              marginBottom: 32,
            }}
          >
            <Button
              onClick={() => setCurrentTab("archive_students")}
              className="modern-add-btn"
              style={{
                ...addButtonStyle,
                backgroundColor: "#1565c0",
                color: "white",
                minWidth: 170,
                minHeight: 60,
              }}
              data-tour="archive-students-button"
            >
              <span
                style={{
                  position: "relative",
                  display: "inline-block",
                  marginLeft: 8,
                }}
              >
                {calcNumberOfArchivedStudents() > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: -50,
                      right: 60,
                      background: "#ff5252",
                      color: "#fff",
                      borderRadius: "50%",
                      minWidth: 30,
                      height: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1em",
                      fontWeight: "bold",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                      zIndex: 2,
                    }}
                  >
                    {calcNumberOfArchivedStudents()}
                  </span>
                )}
              </span>
              <strong>ארכיון ספורטאים</strong>
            </Button>
            <Button
              variant="success"
              onClick={() => setCurrentTab("new_students")}
              className="modern-add-btn"
              style={{ ...addButtonStyle, minWidth: 170, minHeight: 60 }}
              data-tour="new-students-button"
            >
              <span
                style={{
                  position: "relative",
                  display: "inline-block",
                  marginLeft: 8,
                }}
              >
                {calcNumberOfNewStudents() > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: -50,
                      right: 60,
                      background: "#ff5252",
                      color: "#fff",
                      borderRadius: "50%",
                      minWidth: 30,
                      height: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1em",
                      fontWeight: "bold",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                      zIndex: 2,
                    }}
                  >
                    {calcNumberOfNewStudents()}
                  </span>
                )}
              </span>
              <strong>ספורטאים חדשים</strong>
            </Button>
          </div>
        )}
        {/* Students Table Section */}
        {processedStudents && processedStudents.length > 0 && (
          <div style={sectionCardStyle} className="modern-student-card">
            <div
              style={{
                fontWeight: 700,
                fontSize: "1.4rem",
                color: "#1976d2",
                borderRight: "6px solid #1976d2",
                paddingRight: 16,
                marginBottom: 24,
                display: "flex",
                alignItems: "center",
                gap: 10,
                direction: "rtl",
              }}
            >
              <FaUser className="modern-section-header-icon" />
              כל החניכים
            </div>
            <DataTable
              data={processedStudents}
              type={"students"}
              columnMapping={{
                ...(userData?.userField === "martial_arts" && {
                  beltLevel: "חגורה",
                  weightKG: "משקל (ק״ג)",
                }),
                ...(userData?.userField === "ball_games" && {
                  shirtSize: "מידת חולצה",
                  pantsSize: "מידת מכנסיים",
                }),
                address: "כתובת",
                gender: "מין",
                displayGroups: "קבוצות",
                age: "גיל",
                name: "שם החניך",
              }}
              ignoredAttributes={[
                "id",
                "specialFields",
                "beltColor",
                "birthday",
                "classPresenceRecords",
                "firstParentEmailAddress",
                "firstParentName",
                "firstParentNumber",
                "imageURI",
                "imageUploadTime",
                "status",
                "weight",
                "secondParentEmailAddress",
                "secondParentName",
                "secondParentNumber",
                "momPhoneNumber",
                "momEmailAddress",
                "paymentCheckData",
                "IDnumber",
                "studentID",
                "studentCreatedTime",
                "studentSource",
                "createdAt",
                "studentCreatedAt",
                "groupsIDs",
                ...(userData?.userField === "martial_arts"
                  ? ["shirtSize", "pantsSize", "shortsSize"]
                  : []),
                ...(userData?.userField === "ball_games"
                  ? ["beltLevel", "weightKG"]
                  : []),
                "formattedCreatedAt",
              ]}
              onRowClick={onStudentRowClick}
              metaData={groups || []}
              onFilteredDataChange={handleFilteredDataChange}
              className="students-data-table modern-students-table"
              data-tour="students-table"
            />
          </div>
        )}
        <ScrollToTop />
        {/** Student Details Modal */}
        {selectedStudent && (
          <StudentDetailsModal
            isOpen={showStudentDetailsModal}
            onClose={() => {
              setShowStudentDetailsModal(false);
              setIsEditMode(false);
              setEditedStudent(null);
              setSelectedStudent(null);
              setCurrentStudentIndex(-1);
            }}
            student={selectedStudent}
            groups={groups}
            isEditMode={isEditMode}
            editedStudent={editedStudent}
            onEdit={handleEditClick}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
            onDisable={() => setShowDisableConfirm(true)}
            hasEditPrivilege={hasEditPrivilege}
            handleFieldChange={handleFieldChange}
            handleSpecialFieldChange={handleSpecialFieldChange}
            beltColorOptions={beltColorOptions}
            disableLoading={disableLoading}
            showDisableConfirm={showDisableConfirm}
            setShowDisableConfirm={setShowDisableConfirm}
            handleDisableStudent={handleDisableStudent}
            navigateToStudent={navigateToStudent}
            currentStudentIndex={currentStudentIndex}
            processedStudentsLength={processedStudents.length}
            presenceLogs={presenceLogs}
          />
        )}
        {editError && (
          <CustomAlert
            type="error"
            title="שגיאה"
            message={editError}
          />
        )}
        {alert && (
          <CustomAlert
            type={alert.type}
            title={alert.title}
            message={alert.message}
          />
        )}
        {/* Confirm Disable Modal */}
        <CustomModal
          isOpen={showDisableConfirm}
          onClose={() => setShowDisableConfirm(false)}
          title="השבתת חניך"
          subtitle={null}
          width={35}
          height={null}
          footerButtons={[
            {
              title: "ביטול",
              style: "grey",
              onClick: () => setShowDisableConfirm(false),
              disabled: disableLoading,
            },
            {
              title: disableLoading ? "משבית..." : "השבת חניך",
              style: "red",
              onClick: handleDisableStudent,
              disabled: disableLoading,
            },
          ]}
        >
          <div style={{ direction: "rtl", fontSize: 17, textAlign: "center" }}>
            האם אתה בטוח שברצונך להשבית את החניך? פעולה זו תעביר אותו לארכיון.
          </div>
        </CustomModal>
      </div>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        disableScrolling={true}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#0066CC",
            textColor: "#333",
            zIndex: 1000,
          },
          tooltipContainer: {
            direction: "rtl",
            textAlign: "right",
          },
          buttonNext: {
            backgroundColor: "#0066CC",
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonBack: {
            marginRight: 10,
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonSkip: {
            color: "#666",
            fontSize: 14,
            padding: "8px 16px",
          },
        }}
        locale={{
          back: "הקודם",
          close: "סגור",
          last: "סיום",
          next: "הבא",
          skip: "דלג",
        }}
      />
    </div>
  );
}

export default StudentsPane;
